/* eslint-disable react-hooks/exhaustive-deps */
import constate from 'constate'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import { useWorkspaceContext } from './useWorkspace'
import { useZippieIdContext } from './useZippieId'
import apiClient from '../api/apiClient'

export interface IPaymentInformation {
  firstName?: string
  lastName?: string
  country?: string
  phoneNumber?: string
  operator: string
}
interface IOrganizationRole {
  name: string
  description: string
}

// Define the organization structure
interface IOrganization {
  _organizationId: string
  roles?: IOrganizationRole[]
  primary: boolean
}
export interface IUser {
  id?: string
  displayName?: string
  firstName?: string
  lastName?: string
  roles?: string[]
  phoneNumber?: string
  email?: string
  location?: string
  description?: string
  status?: string
  paymentInfo?: IPaymentInformation
  _pictureId?: string
  balanceTZS?: string
  userId?: string | null
  createdAt?: string
  updatedAt?: string
  picture?: {
    url: string
    ext: string
    id: string
  }
  organizations?: IOrganization[]
}

interface IUserResponse {
  _id?: string
  _workspaceId?: string
  _pictureId?: string
  firstName?: string
  lastName?: string
  _userId?: string
  roles?: string[]
  displayName?: string
  phoneNumber?: string
  email?: string
  location?: string
  paymentInfo?: IPaymentInformation
  description?: string
  balanceTZS?: string
  status?: string
  picture: {
    url: string
    ext: string
    id: string
  }
}

interface ICreateNewEmployeeResponse {
  data: any
}

interface IGetEmployeesResponse {
  data: {
    data: IUserResponse[]
    count: number
  }
}

export interface IMemberResponse {
  _userId: string
  _workspaceId: string
  deactivated: boolean
  roles: {
    name: string
    scopes: []
    _id: string
    _projectId: string[]
  }[]
  user: {
    email: string
    name: string
  }
}

interface IGetMembersResponse {
  data: {
    data: IMemberResponse[]
    count: number
  }
}

export interface IDashboardUser {
  email?: string
  name?: string
  id?: string
  userId?: string
  nodeRoleId?: string
  status?: 'active' | 'deactivated'

  workspaceRoles?: {
    _id?: string
    createdAt?: string
    updatedAt?: string
    name?: string
  }[]
}

export interface IInviteUserPayload {
  email: string
  name: string

  roles: {
    _id: string
    _projectId: string[]
    scopes: []
  }[]
}
export interface IRole {
  name: string
  type: string
  _id: string
  _projectId?: string[]
  scopes?: []
}
export const useUsers = () => {
  const [dashboardUsers, setDashboardUsers] = useState<IDashboardUser[]>([])
  const [users, setUsers] = useState<IUser[]>([])
  const [availableRoles, setAvailableRoles] = useState<IRole[]>([])

  const [userCount, setUserCount] = useState(0)
  const [selectedId, setSelectedId] = useState<string | undefined>('')
  const [selectedUserId, setSelectedUserId] = useState<string | null | undefined>('')
  const [isUserLinked, setIsUserLinked] = useState<boolean>(false)
  const [isWorkspaceOwner, setIsWorkspaceOwner] = useState<boolean>()
  const [isWorkspaceAdmin, setIsWorkspaceAdmin] = useState<boolean>()
  const [userDashboardCount, setUserDashboardCount] = useState(0)

  const { userId } = useZippieIdContext()
  const [queryOptions, setQueryOption] = useState({
    sortBy: 'createdAt,-1',
    skip: 0,
    limit: 50,
    status: ['active', 'draft'],
    query: undefined,
  })
  const { workspaceId } = useParams()

  const { isWorkspaceFetchingInProgress, selectedWorkspaceDetails, workspaces, selectedNodeId } = useWorkspaceContext()
  const defaultWorkspaceId = workspaceId || selectedNodeId

  const inviteNewDashboardUser = async (user: IInviteUserPayload) => {
    const { data } = await apiClient.post<any, ICreateNewEmployeeResponse>(
      `/workspace/${workspaceId}/user_invite`,
      user,
    )
  }

  const fetchRoles = async (nodeId: number | string) => {
    const { data } = await apiClient.get(`/workspace/${nodeId}/roles`)
    setAvailableRoles(data.data)
  }
  const fetchDashboardUsersInvites = async (params: {
    sortBy?: string
    skip?: number
    limit?: number
    status?: string[]
    query?: string
  }) => {
    const { data } = await apiClient.get(`/workspace/${workspaceId}/user_invites`, { params })
    return data
  }

  const revokeDashboardUsersInvite = async (inviteId: string) => {
    const { data } = await apiClient.post(`/workspace/${workspaceId}/user_invite/${inviteId}/revoke`)
    return data
  }
  const resendDashboardUsersInvite = async (inviteId: string) => {
    const { data } = await apiClient.post(`/workspace/${workspaceId}/user_invite/${inviteId}/resend`)
    return data
  }
  const fetchDashboardUsers = async (params: {
    sortBy?: string
    skip?: number
    limit?: number
    status?: string[]
    query?: string
  }) => {
    const response = await apiClient.get<any, IGetMembersResponse>(`/workspace/${defaultWorkspaceId}/user`, {
      params,
    })

    return response.data
  }

  const updateDashboardUser = async ({ _id, roles }: { _id: string; roles?: IRole[] }) => {
    await apiClient.patch(`/workspace/${workspaceId}/user/${_id}`, {
      roles,
    })
  }
  // Any used for roles due to absence of type property in the passed roles /
  // conditional chaining in the IRole interface results in errors
  // Removed status from the path, as this route doesn't exist in the BE
  const handleDashboardUserActivation = async ({
    deactivated,
    _id,
    roles,
  }: {
    deactivated: boolean
    _id: string
    roles: any
  }) => {
    await apiClient.patch(`/workspace/${workspaceId}/user/${_id}`, {
      deactivated,
      roles,
    })
  }
  const fetchUsers = async () => {
    const { data } = await apiClient.get<any, IGetEmployeesResponse>(`/workspace/${defaultWorkspaceId}/employees`, {
      params: queryOptions,
    })
    const formatData = data.data.map(({ _id, _pictureId, _userId, picture, ...rest }) => ({
      id: _id,
      _pictureId: picture?.url,
      userId: _userId,
      picture,
      ...rest,
    }))
    setUserCount(data.count)
    setUsers(formatData)
  }

  const getDashboardUsers = async () => {
    const users = await fetchDashboardUsers({ limit: 50, skip: 0 })
    const members: IDashboardUser[] = users.data.map((member) => ({
      email: member.user.email,
      name: member.user.name,
      id: member._userId,
      userId: member._userId,
      nodeRoleId: member.roles && member.roles.length !== 0 ? member.roles[0].name : '',
      status: member.roles && member.roles.length !== 0 ? 'active' : 'deactivated',
      workspaceRoles: member.roles,
    }))
    const currentUserDetails = members.find((member) => member.id === userId)
    setIsWorkspaceAdmin(
      currentUserDetails?.workspaceRoles?.some((role) => role.name?.toLocaleLowerCase() === 'admin') || false,
    )
    setDashboardUsers(members)
    setUserDashboardCount(users.count)
  }
  useEffect(() => {
    if (!isWorkspaceFetchingInProgress && userId && selectedWorkspaceDetails) {
      selectedWorkspaceDetails.subscription?.data && fetchUsers()
      selectedWorkspaceDetails.subscription?.data && fetchRoles(defaultWorkspaceId || '')
      getDashboardUsers()
      // to move this logic to the workspace context
      setIsWorkspaceOwner(selectedWorkspaceDetails.userId === userId)
    }
  }, [isWorkspaceFetchingInProgress, queryOptions, userId, selectedWorkspaceDetails])

  const addNewUser = async (userDetails: IUser) => {
    const { data } = await apiClient.post<any, ICreateNewEmployeeResponse>(`/workspace/${workspaceId}/employee`, {
      firstName: userDetails.firstName || '',
      lastName: userDetails.lastName || '',
      displayName: userDetails.displayName,
      description: userDetails.description,
      location: userDetails.location,
      _pictureId: userDetails._pictureId,
      roles: userDetails.roles,
      phoneNumber: userDetails.phoneNumber,
      email: userDetails.email,
      status: 'active',
      organizations: userDetails.organizations,
    })
    fetchUsers()
  }

  const updateUser = async (userDetails: IUser) => {
    await apiClient.patch(`/workspace/${workspaceId}/employee/${userDetails.id}`, {
      ...userDetails,
    })
    await fetchUsers()
  }

  const onQueryChange = (value: string | string[], name: string) => {
    if (name === 'sortBy' || name === 'status') {
      setQueryOption({ ...queryOptions, [name]: value as string[] })
    } else {
      const updatedOptions: any = { ...queryOptions, [name]: value }

      if (name !== 'status') {
        updatedOptions.status = ''
      }

      setQueryOption(updatedOptions)
    }
  }

  const deleteUser = async (userId: string | undefined) => {
    await apiClient.delete(`/workspace/${workspaceId}/employee/${userId}`)
    fetchUsers()
  }

  const deleteDashboardUser = async (userId: number | string) => {
    await apiClient.delete(`/workspace/${workspaceId}/user/${userId}`)
  }

  return {
    users,
    addNewUser,
    updateUser,
    deleteUser,
    inviteNewDashboardUser,
    dashboardUsers,
    deleteDashboardUser,
    fetchDashboardUsers,
    userCount,
    queryOptions,
    setQueryOption,
    onQueryChange,
    selectedId,
    setSelectedId,
    setSelectedUserId,
    selectedUserId,
    isUserLinked,
    setIsUserLinked,
    isWorkspaceOwner,
    isWorkspaceAdmin,
    availableRoles,
    getDashboardUsers,
    updateDashboardUser,
    fetchDashboardUsersInvites,
    revokeDashboardUsersInvite,
    resendDashboardUsersInvite,
    handleDashboardUserActivation,
    userDashboardCount,
  }
}

const [UsersContainer, useUsersContext] = constate(useUsers)

export { UsersContainer, useUsersContext }
